import React from 'react';
import { Globe, ChevronDown, MapPin, Mail, Linkedin } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Footer = () => {
  const { t, currentLang, setLang, languages } = useLanguage();
  const currentYear = new Date().getFullYear();

  // Multi-language footer content
  const footerContent = {
    en: {
      tagline: 'Active investment, revenue acceleration, and institutional brand valuation — for founders and investors ready to perform.',
      location: 'Madrid, Spain · Est. 2026',
      address: 'Madrid, Spain',
      quickLinks: 'Quick Links',
      services: 'Services',
      approach: 'Approach',
      forInvestors: 'For Investors',
      about: 'About',
      faq: 'FAQ',
      contact: 'Contact',
      contactTitle: 'Contact',
      byAppointment: 'Available by appointment',
      language: 'Language',
      allRights: 'All rights reserved.',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      regulatoryNotice: 'Regulatory Notice: HÜBNER Capital does not provide regulated financial advice. All investment activities are conducted in accordance with applicable law.'
    },
    it: {
      tagline: 'Investimento attivo, accelerazione dei ricavi e valutazione istituzionale del brand — per founder e investitori pronti a performare.',
      location: 'Madrid, Spagna · Est. 2026',
      address: 'Madrid, Spagna',
      quickLinks: 'Link Rapidi',
      services: 'Servizi',
      approach: 'Approccio',
      forInvestors: 'Per Investitori',
      about: 'Chi Siamo',
      faq: 'FAQ',
      contact: 'Contatto',
      contactTitle: 'Contatto',
      byAppointment: 'Disponibile su appuntamento',
      language: 'Lingua',
      allRights: 'Tutti i diritti riservati.',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Termini di Servizio',
      regulatoryNotice: 'Avviso Regolamentare: HÜBNER Capital non fornisce consulenza finanziaria regolamentata. Tutte le attività di investimento sono condotte in conformità con la legge applicabile.'
    },
    'es-es': {
      tagline: 'Inversión activa, aceleración de ingresos y valoración institucional de marca — para founders e inversores listos para rendir.',
      location: 'Madrid, España · Est. 2026',
      address: 'Madrid, España',
      quickLinks: 'Enlaces Rápidos',
      services: 'Servicios',
      approach: 'Enfoque',
      forInvestors: 'Para Inversores',
      about: 'Nosotros',
      faq: 'FAQ',
      contact: 'Contacto',
      contactTitle: 'Contacto',
      byAppointment: 'Disponible con cita previa',
      language: 'Idioma',
      allRights: 'Todos los derechos reservados.',
      privacyPolicy: 'Política de Privacidad',
      termsOfService: 'Términos de Servicio',
      regulatoryNotice: 'Aviso Regulatorio: HÜBNER Capital no proporciona asesoramiento financiero regulado. Todas las actividades de inversión se realizan de acuerdo con la ley aplicable.'
    },
    'es-latam': {
      tagline: 'Inversión activa, aceleración de ingresos y valuación institucional de marca — para founders e inversores listos para rendir.',
      location: 'Madrid, España · Est. 2026',
      address: 'Madrid, España',
      quickLinks: 'Enlaces Rápidos',
      services: 'Servicios',
      approach: 'Enfoque',
      forInvestors: 'Para Inversores',
      about: 'Nosotros',
      faq: 'FAQ',
      contact: 'Contacto',
      contactTitle: 'Contacto',
      byAppointment: 'Disponible con cita previa',
      language: 'Idioma',
      allRights: 'Todos los derechos reservados.',
      privacyPolicy: 'Política de Privacidad',
      termsOfService: 'Términos de Servicio',
      regulatoryNotice: 'Aviso Regulatorio: HÜBNER Capital no proporciona asesoramiento financiero regulado. Todas las actividades de inversión se realizan de acuerdo con la ley aplicable.'
    }
  };

  const fc = footerContent[currentLang] || footerContent.en;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer 
      data-testid="footer"
      className="relative bg-[#1A3A4A]"
    >
      {/* Teal top border */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#60B5AF]" />
      
      {/* Main Footer Content */}
      <div className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-14">
            {/* Logo & Description */}
            <div className="md:col-span-1">
              <a 
                href="/" 
                className="block mb-5"
              >
                <div className="flex flex-col">
                  <span className="font-heading text-2xl font-bold text-white tracking-tight">
                    HÜBNER <span className="font-normal text-white/80">Capital</span><sup className="text-[#60B5AF] text-xs ml-0.5">™</sup>
                  </span>
                </div>
              </a>
              <p className="font-body text-white/60 leading-relaxed text-sm mb-4">
                {fc.tagline}
              </p>
              {/* Social Proof Line */}
              <p className="font-body text-xs text-white/40 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {fc.location}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#60B5AF] mb-5">
                {fc.quickLinks}
              </h4>
              <ul className="space-y-3">
                {[
                  { label: fc.services, id: 'services' },
                  { label: fc.approach, id: 'approach' },
                  { label: fc.forInvestors, id: 'investors' },
                  { label: fc.about, id: 'about' },
                  { label: fc.faq, id: 'faq' },
                  { label: fc.contact, id: null, action: 'contact' },
                ].map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => {
                        if (link.action === 'contact') {
                          // Scroll to top and open contact modal would be handled by parent
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        } else {
                          scrollToSection(link.id);
                        }
                      }}
                      className="font-body text-sm text-white/60 hover:text-[#60B5AF] transition-colors"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#60B5AF] mb-5">
                {fc.contactTitle}
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#60B5AF] flex-shrink-0 mt-0.5" />
                  <span className="font-body text-sm text-white/60">
                    Calle Roma, 1<br />
                    {fc.address}
                  </span>
                </li>
                <li>
                  <a 
                    href="mailto:contact@hubner.capital"
                    className="flex items-center gap-3 font-body text-sm text-[#60B5AF] hover:underline underline-offset-2 transition-colors group"
                  >
                    <Mail className="h-5 w-5 text-[#60B5AF] flex-shrink-0" />
                    <span>contact@hubner.capital</span>
                  </a>
                  <p className="font-body text-xs text-white/40 italic mt-2 ml-8">
                    {fc.byAppointment}
                  </p>
                </li>
                <li>
                  <a 
                    href="https://linkedin.com/in/adilsonsequeira"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 font-body text-sm text-white/60 hover:text-[#60B5AF] transition-colors"
                  >
                    <Linkedin className="h-5 w-5 text-[#60B5AF] flex-shrink-0" />
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>

            {/* Language Switcher */}
            <div>
              <h4 className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#60B5AF] mb-5">
                {fc.language}
              </h4>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    data-testid="footer-language-switcher"
                    className="flex items-center gap-2 font-body text-sm text-white/60 hover:text-[#60B5AF] transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    <span>{languages.find(l => l.code === currentLang)?.name}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-white z-[150]">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLang(lang.code)}
                      className={`cursor-pointer ${currentLang === lang.code ? 'bg-muted' : ''}`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Separator line */}
          <div className="border-t border-white/10 mb-8" />

          {/* Bottom Bar - Legal */}
          <div className="space-y-4">
            {/* Copyright and Links Row */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="font-body text-sm text-white/50">
                © {currentYear} HÜBNER Capital™. {fc.allRights}
              </p>
              <div className="flex gap-6">
                <a href="#" className="font-body text-sm text-white/50 hover:text-[#60B5AF] transition-colors">
                  {fc.privacyPolicy}
                </a>
                <a href="#" className="font-body text-sm text-white/50 hover:text-[#60B5AF] transition-colors">
                  {fc.termsOfService}
                </a>
                <a href="/admin" className="font-body text-sm text-white/20 hover:text-[#60B5AF] transition-colors">
                  Admin
                </a>
              </div>
            </div>

            {/* Regulatory Notice */}
            <p className="font-body text-xs text-white/30 text-center md:text-left max-w-4xl">
              {fc.regulatoryNotice}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
